import { useMemo, useRef, useState } from "react";
import { useAppDispatch } from "hooks/useAppDispatch";
import { generateCSV } from "utils/csv";
import { downloadFile } from "utils/downloads";
import Papa from "papaparse";
import { removeDuplicatedElements } from "utils/Array";
import {
  useAddGraduationClassStudentsMutation,
  useAddUserMutation,
  useGetUsersQuery,
} from "graphql/__generated__/hooks";
import { registerThunk } from "reduxStore/auth/thunks";
import { ROLES, User } from "types/user";
import { useParams } from "react-router-dom";
import { useClinicText } from "hooks/useClinicText";
import { Organization } from "types/organization";
import { normalizeEmail, normalizeImportedUserRow } from "utils/email";

const COLUMNS = ["name", "email"];

type StudentData = {
  name: string;
  email: string;
};

type LoadingData = { current: number; total: number };

type ReportData = {
  success: string[];
  error: string[];
  alreadyExists: string[];
};

interface UseAddStudentsStepProps {
  graduationClassId?: string;
  organization?: Organization;
  onGraduationClassStudentsAdded?: (students: string[]) => void;
}

export const useAddStudentsStep = ({
  graduationClassId,
  organization,
  onGraduationClassStudentsAdded,
}: UseAddStudentsStepProps) => {
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [displayData, setDisplayData] = useState<boolean>(false);
  const [data, setData] = useState<StudentData[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingData, setLoadingData] = useState<LoadingData>({
    current: 0,
    total: 0,
  });
  const [report, setReport] = useState<ReportData>({
    success: [],
    error: [],
    alreadyExists: [],
  });
  const { textGraduationClass } = useClinicText(false, organization);
  const [isAddStudentModalOpen, setIsAddStudentModalOpen] = useState(false);

  const { organizationId } = useParams();
  const selectedData = useRef<string[]>([]);
  const dispatch = useAppDispatch();
  const { data: users } = useGetUsersQuery();
  const [addGraduationClassStudents] = useAddGraduationClassStudentsMutation();
  const [createUser] = useAddUserMutation();

  const usersEmails = useMemo<string[]>(() => {
    return users?.user.map((u) => normalizeEmail(u.email)) || [];
  }, [users]);

  const usersDict = useMemo<{ [k: string]: User }>(() => {
    return (
      users?.user.reduce(
        (acc, user) => {
          const email = normalizeEmail(user.email);
          acc[email] = user as User;
          return acc;
        },
        {} as { [k: string]: User },
      ) || {}
    );
  }, [users]);

  const handleGraduationClass = async (usersId: string[]) => {
    if (!graduationClassId) return;

    const dataGraduationClassStudents = usersId.map((user_id) => ({
      student_id: user_id,
      graduation_class_id: graduationClassId,
    }));

    await addGraduationClassStudents({
      variables: { objects: dataGraduationClassStudents },
    });

    return usersId;
  };

  const onSubmit = async () => {
    setError(null);
    setLoading(true);
    let filteredData: StudentData[] = data;
    const dictData: { [k: string]: StudentData } = {};
    const emailsAlreadyExists: string[] = [];
    const successElements: string[] = [];
    const errorElements: string[] = [];
    let userIds: string[] = [];
    let newUserIds: string[] = [];
    const addedStudents: User[] = [];

    if (!organizationId) {
      setError("Organization ID is required");
      setLoading(false);
      return;
    }

    if (!graduationClassId) {
      setError(`${textGraduationClass} ID is required`);
      setLoading(false);
      return;
    }

    // handle selected data
    if (selectedData.current.length > 0) {
      data.forEach((d) => {
        dictData[normalizeEmail(d.email)] = normalizeImportedUserRow(d);
      });
      filteredData = selectedData.current.map(
        (email) => dictData[normalizeEmail(email)],
      );
    }

    // filter elements that already exists in DB
    filteredData = filteredData.filter((d) => {
      const email = normalizeEmail(d.email);
      const exists = usersEmails.includes(email);
      if (exists) {
        emailsAlreadyExists.push(email);
        if (usersDict[email]) {
          userIds.push(usersDict[email]?.user_id as string);
          addedStudents.push(usersDict[email]);
        }
      }
      return !exists;
    });

    // upload new users
    setLoadingData({ current: 0, total: filteredData.length });
    for (const data of filteredData) {
      const email = normalizeEmail(data.email);
      const name = data.name.trim();
      const result = await dispatch(
        registerThunk({
          email,
          role: ROLES.STUDENT,
          name,
          sendInvite: true,
        }),
      );
      const payload = result.payload as { status: string; user_id: string };

      if (payload.status === "success") {
        successElements.push(email);
        await createUser({
          variables: {
            user_id: payload.user_id,
            email,
            role: ROLES.STUDENT,
            name,
            org_id: organizationId,
          },
        });
        newUserIds.push(payload.user_id);
        addedStudents.push({
          user_id: payload.user_id,
          email,
          name,
          role: ROLES.STUDENT,
        } as User);
      } else {
        errorElements.push(email);
      }
      setLoadingData((prev) => ({
        current: prev.current + 1,
        total: prev.total,
      }));
    }

    // set report
    setReport({
      success: successElements,
      error: errorElements,
      alreadyExists: emailsAlreadyExists,
    });

    // Add students to graduation class
    if (graduationClassId) {
      const allUserIds = [...userIds, ...newUserIds];
      await handleGraduationClass(allUserIds);
    }

    // Call the callback with the added students
    if (onGraduationClassStudentsAdded) {
      onGraduationClassStudentsAdded(successElements);
    }

    setLoading(false);
    setCompleted(true);
  };

  const handleContinue = (file: File) => {
    if (file) {
      const reader = new FileReader();

      reader.onload = (e) => {
        if (e.target?.result) {
          const csvData = e.target.result as string;

          Papa.parse(csvData, {
            header: true,
            complete: (results: any) => {
              const parsedData = results.data.map((row: StudentData) =>
                normalizeImportedUserRow(row),
              );

              // remove duplicated elements
              const parsedDataFiltered = removeDuplicatedElements(
                parsedData,
                "email",
              );

              setData(parsedDataFiltered);
              selectedData.current = parsedDataFiltered.map((d) => d.email);
              setDisplayData(true);
            },
            error: (err: Error) => {
              console.error("Error parsing CSV:", err);
              setError("Error parsing CSV file");
            },
          });
        }
      };

      reader.readAsText(file);
    }
  };

  const handleDownloadClick = () => {
    const data = [
      { name: "Jane Smith", email: "jane.smith@example.com" },
      { name: "John Doe", email: "john.doe@example.com" },
    ];
    const csvContent = generateCSV(data, COLUMNS);
    downloadFile(
      csvContent,
      "students_template.csv",
      "text/csv;charset=utf-8;",
    );
  };

  const resetForm = () => {
    setDisplayData(false);
    setData([]);
    setFile(null);
    setError(null);
    setCompleted(false);
    selectedData.current = [];
  };

  const handleOpenAddStudentModal = () => {
    setIsAddStudentModalOpen(true);
  };

  const handleCloseAddStudentModal = () => {
    setIsAddStudentModalOpen(false);
  };

  const handleAddStudent = (student: { name: string; email: string }) => {
    // If displayData is false, set it to true
    if (!displayData) {
      setDisplayData(true);
    }

    const newStudent = normalizeImportedUserRow({
      name: student.name,
      email: student.email,
    });

    // Check if student already exists to avoid duplicates
    const existingStudent = data.find((s) => s.email === newStudent.email);
    if (!existingStudent) {
      // Add to data array
      setData((prevData) => [...prevData, newStudent]);
      // Add to selectedData as well
      selectedData.current.push(newStudent.email);
    }
  };

  const handleAddFromCSV = (file: File) => {
    if (file) {
      const reader = new FileReader();

      reader.onload = (e) => {
        if (e.target?.result) {
          const csvData = e.target.result as string;

          Papa.parse(csvData, {
            header: true,
            complete: (results: any) => {
              const parsedData = results.data;

              const filteredParsedData = parsedData
                .filter((row: any) => row.email && row.email.trim() !== "")
                .map((row: StudentData) => normalizeImportedUserRow(row));

              // Remove duplicated elements from the parsed data
              const parsedDataFiltered = removeDuplicatedElements(
                filteredParsedData,
                "email",
              );

              if (parsedDataFiltered.length > 0) {
                // If displayData is false, set it to true
                if (!displayData) {
                  setDisplayData(true);
                }

                // Merge with existing data, avoiding duplicates
                setData((prevData) => {
                  const existingEmails = prevData.map((s) => s.email);
                  const newStudents = parsedDataFiltered.filter(
                    (student: StudentData) =>
                      !existingEmails.includes(student.email),
                  );

                  // Add new emails to selectedData
                  newStudents.forEach((student: StudentData) => {
                    if (!selectedData.current.includes(student.email)) {
                      selectedData.current.push(student.email);
                    }
                  });

                  return [...prevData, ...newStudents];
                });
              }
            },
            error: (err: Error) => {
              console.error("Error parsing CSV:", err);
              setError("Error parsing CSV file");
            },
          });
        }
      };

      reader.readAsText(file);
    }
  };

  console.log("~~~report:");
  console.log(report);
  return {
    completed,
    data,
    displayData,
    error,
    file,
    loading,
    loadingData,
    report,
    selectedData,
    setFile,
    setData,
    handleDownloadClick,
    handleContinue,
    setDisplayData,
    onSubmit,
    resetForm,
    isAddStudentModalOpen,
    handleOpenAddStudentModal,
    handleCloseAddStudentModal,
    handleAddStudent,
    handleAddFromCSV,
  };
};
